import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import produccionService, { DesviosConsumoProduccionDto } from '../../services/produccionService';
import { formatCurrency } from '../../utils/formatters';

const origenColor = (origen: string): 'default' | 'success' | 'warning' | 'info' => {
  switch (origen) {
    case 'Agregado':
      return 'info';
    case 'Omitido':
      return 'warning';
    default:
      return 'default';
  }
};

const formatNum = (n: number) =>
  Number(n).toLocaleString('es-AR', { maximumFractionDigits: 4 });

const desvioColor = (n: number) =>
  n > 0 ? 'warning.main' : n < 0 ? 'success.main' : 'inherit';

const DesviosConsumoProduccion: React.FC = () => {
  const { id } = useParams();
  const codigo = Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<DesviosConsumoProduccionDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!codigo) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await produccionService.getDesviosConsumo(codigo);
        if (!cancelled) setData(res);
      } catch (error: unknown) {
        const msg =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No se pudieron cargar los desvíos de consumo';
        enqueueSnackbar(msg, { variant: 'error' });
        if (!cancelled) navigate(`/produccion/ordenes/${codigo}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [codigo, enqueueSnackbar, navigate]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate(`/produccion/ordenes/${codigo}`)} sx={{ textTransform: 'none' }}>
              Orden
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Desvíos de consumo #{codigo}
            </Typography>
          </Stack>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && data && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                PT: {data.codigoArticulo} | Receta: {data.codigoReceta ?? '—'} | Cantidad producida:{' '}
                {data.cantidadProducida}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estimado = BOM de receta escalado por batch. Real = movimientos CONSUMO PRODUCCION. Desvío = real −
                estimado. Costos valorizados con costo unitario del artículo (o PrecioCosto del movimiento).
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  p: 2,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Costo estimado
                  </Typography>
                  <Typography variant="h6">{formatCurrency(data.costoTotalEstimado)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Costo real
                  </Typography>
                  <Typography variant="h6">{formatCurrency(data.costoTotalReal)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Desvío total
                  </Typography>
                  <Typography variant="h6" sx={{ color: desvioColor(data.desvioTotalPesos), fontWeight: 700 }}>
                    {formatCurrency(data.desvioTotalPesos)}
                    {data.desvioTotalPorcentaje == null
                      ? ''
                      : ` (${formatNum(data.desvioTotalPorcentaje)} %)`}
                  </Typography>
                </Box>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Estimado</TableCell>
                    <TableCell align="right">Real</TableCell>
                    <TableCell align="right">Desvío</TableCell>
                    <TableCell align="right">Costo est.</TableCell>
                    <TableCell align="right">Costo real</TableCell>
                    <TableCell align="right">Desvío $</TableCell>
                    <TableCell align="right">Desvío %</TableCell>
                    <TableCell>Origen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lineas.map((l) => (
                    <TableRow key={l.codigoArticulo}>
                      <TableCell>
                        {l.codigoArticulo} — {l.descripcion}
                      </TableCell>
                      <TableCell align="right">{formatNum(l.cantidadEstimada)}</TableCell>
                      <TableCell align="right">{formatNum(l.cantidadReal)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: desvioColor(l.desvio), fontWeight: l.desvio !== 0 ? 600 : 400 }}
                      >
                        {formatNum(l.desvio)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(l.costoEstimado)}</TableCell>
                      <TableCell align="right">{formatCurrency(l.costoReal)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: desvioColor(l.desvioPesos), fontWeight: l.desvioPesos !== 0 ? 600 : 400 }}
                      >
                        {formatCurrency(l.desvioPesos)}
                      </TableCell>
                      <TableCell align="right">
                        {l.desvioPorcentaje == null ? '—' : `${formatNum(l.desvioPorcentaje)} %`}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={l.origen} color={origenColor(l.origen)} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.lineas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary">
                          No hay líneas de consumo para comparar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                {data.lineas.length > 0 && (
                  <TableFooter>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Totales</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(data.costoTotalEstimado)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(data.costoTotalReal)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: desvioColor(data.desvioTotalPesos) }}
                      >
                        {formatCurrency(data.desvioTotalPesos)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {data.desvioTotalPorcentaje == null
                          ? '—'
                          : `${formatNum(data.desvioTotalPorcentaje)} %`}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DesviosConsumoProduccion;
